<script lang="ts" setup>

  import type { GolfCard } from '~/core/types/golf-card.type'

  import { BngHoles } from '../../holes'
  import { BngCardTopInfos } from '../card-top-infos'

  type Props = {
    golf: GolfCard
    isFavorite?: boolean
    departures?: boolean
  }
  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'toggleFavorite'): void
  }>()

  const { rate, services } = useGolfCard(props.golf)
  const route = useRoute()
</script>
<template>
  <NuxtLink
    :to="{name: 'golf-golfSlug', params: { golfSlug: props.golf.slug }, query: {date: route.query.date, players: route.query.players}}"
    class="c-golf-result-card card"
  >
    <bng-card-top-infos
      v-if="props.departures"
      :main="golf.departure_count + ' départs disponibles'"
    />
    <div
      class="picture"
      :style="{ 'background-image': `url(${golf.thumb})` }"
    >
      <button
        class="favorite"
        @click.prevent="() => emit('toggleFavorite')"
      >
        <SvgIcon
          :class="{ active: props.isFavorite }"
          icon="favorite"
          :width="18"
          :height="16"
          :desktop-width="18"
          :desktop-height="16"
        />
      </button>
    </div>

    <div class="content">
      <div class="description">
        <div class="name">
          <span>{{ props.golf.name }}</span>
        </div>
        <div class="location">
          <span>{{ props.golf.commune }}, {{ props.golf.region }}</span>
        </div>
        <div class="rates">
          <template v-if="golf.rating">
            <SvgIcon
              icon="star"
              :width="32"
              :height="32"
              :desktop-width="32"
              :desktop-height="32"
            />
            <span class="note">
              <strong>{{ golf.rating }}</strong>/5
            </span>
            <span class="rates-count"> ({{ rate }} notes) </span>
          </template>
          <template v-else>
            <span class="no-rate">Aucun avis</span>
          </template>
        </div>
        <BngHoles
          small
          :model-value="props.golf.holes ?? []"
        />
        <div class="detail">
          <span>{{ services }}</span>
        </div>
      </div>
      <div class="pricing">
        <div class="rates">
          <template v-if="golf.rating">
            <SvgIcon
              icon="star"
              :width="32"
              :height="32"
              :desktop-width="32"
              :desktop-height="32"
            />
            <span class="note">
              <strong>{{ golf.rating }}</strong>/5
            </span>
            <span class="rates-count"> ({{ rate }} notes) </span>
          </template>
          <template v-else>
            <span class="no-rate">Aucun avis</span>
          </template>
        </div>
        <div class="has-promotions">
          <SvgIcon
            :class="{ hidden: !golf.has_promotions }"
            icon="promotion"
            :width="32"
            :height="32"
            :desktop-width="36"
            :desktop-height="36"
          />
        </div>
        <div
          v-if="golf.best_price"
          class="prices"
        >
          <div class="starting-price">
            <span>à partir de</span>
          </div>
          <div class="price">
            <span>{{ golf.best_price.toFixed(2) }}€</span>
          </div>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>
<style scoped>

.c-golf-result-card {
  position: relative;
  z-index: 0;
  display: flex;
  overflow: hidden;
  max-width: 100%;
  flex: 0 1 100%;
  flex-direction: column;
  padding: 0.75em 0 0;

  .picture {
    position: relative;
    width: 100%;
    height: auto;
    flex: 0 0 auto;
    align-self: flex-start;
    border-radius: 4px;
    aspect-ratio: 3 / 2;
    background-color: var(--color-secondary);
    background-position: center;
    background-size: cover;

    button {
      position: absolute;
      right: 1em;
      bottom: 1em;
      display: flex;
      width: 2.125em;
      height: 2.125em;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: none;
      border-radius: 50%;
      background-color: white;
      font-size: 1em;

      .c-icon {
        color: var(--color-secondary);
        opacity: 0.2;

        &.active {
          color: var(--color-primary);
          opacity: 1;
        }
      }
    }
  }

  .content {
    display: flex;
    overflow: hidden;
    flex: 1 1 auto;
    flex-direction: row;
    justify-content: space-between;
    padding-top: 0.5em;

    .rates {
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      padding: 0.25em 0;

      .c-icon {
        margin-right: 0.25em;
        color: var(--color-primary);
      }

      .note,
      .rates-count,
      .no-rate {
        margin: 0 0.25em;
        color: var(--color-secondary);
        font-family: Open-Sans, sans-serif;
        font-size: 0.875em;
        font-weight: 500;
        line-height: 1rem;
        opacity: 0.8;

        strong {
          color: var(--color-secondary);
          font-family: Bariol, sans-serif;
          font-size: 1.875em;
          font-weight: 700;
        }

        &.no-rate {
          margin: 0;
        }
      }
    }

    .description {
      display: flex;
      min-width: 0;
      flex: 1 1 auto;
      flex-direction: column;
      justify-content: space-between;
      padding-right: 1em;

      .name {
        span {
          color: var(--color-secondary);
          font-family: Bariol, sans-serif;
          font-size: 1.5em;
          font-weight: bold;
        }
      }

      .location {
        opacity: 0.8;

        span {
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 1em;
          font-weight: 600;
        }
      }

      .rates {
        display: none;
      }

      .c-holes {
        margin: 0.25em 0;
      }

      .detail {
        display: flex;
        width: 100%;
        flex-direction: row;
        padding-bottom: 0.25em;
        opacity: 0.6;

        span {
          display: inline-block;
          overflow: hidden;
          width: 100%;
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 0.875em;
          font-weight: normal;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    .pricing {
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      align-items: flex-end;

      .rates {
        display: flex;
      }

      .has-promotions {
        .c-icon {
          color: var(--color-promo);

          &.hidden {
            opacity: 0;
          }
        }
      }

      .prices {
        .starting-price {
          opacity: 0.6;
          text-align: right;
          white-space: nowrap;

          span {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 0.875em;
            font-weight: normal;
          }
        }

        .price {
          span {
            color: var(--color-primary);
            font-family: Bariol, sans-serif;
            font-size: 2.75em;
            font-weight: 600;
          }
        }
      }
    }
  }

  @media screen and (width >= 1024px) {
    flex-direction: row;

    .picture {
      width: 40%;
      aspect-ratio: 4 / 3;
    }

    .content {
      padding-top: 0;
      margin-left: 1.5em;

      .description {
        .c-holes {
          margin: 0.5em 0;
        }

        .rates {
          display: flex;
        }
      }

      .pricing {
        .has-promotions {
          .c-icon {
            margin-top: 0.5em;
          }
        }

        .rates {
          display: none;
        }

        .prices {
          .price {
            span {
              font-size: 3.125em;
            }
          }
        }
      }
    }
  }
}
</style>
